import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useBlogPostViewers } from "@/queries/blog.query.ts";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface BlogPostViewersModalProps extends ModalProps {
  postId: number;
}

export function BlogPostViewersModal({ postId, ...props }: BlogPostViewersModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { renderDateAndTime, renderRelativeDate } = useFormatter();
  const loaderAim = useRef<HTMLDivElement>(null);
  const {
    data: viewersPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBlogPostViewers({
    post_id: postId,
    page_number: 1,
  });

  const flatData = useMemo(
    () =>
      viewersPages?.pages?.flatMap((viewersData) => {
        if (!viewersData) return [];

        return viewersData.items;
      }) ?? [],
    [viewersPages]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          await fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "400px" }
    );

    if (loaderAim.current) {
      observer.observe(loaderAim.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <Modal
      {...props}
      title={t("blog:viewers")}
      withCloser
      closeByEscEnabled
      layout="stretch"
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <Button onClick={() => close(id)} size={"lg"} className={"w-full"}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        {flatData.map((employee) => (
          <EmployeeListItem
            employee={employee}
            valueSlot={
              <Tooltip>
                <TooltipTrigger>
                  <Text className={"text-secondary"}>
                    {renderRelativeDate(employee.datetime_viewed_at)}
                  </Text>
                </TooltipTrigger>
                <TooltipContent>{renderDateAndTime(employee.datetime_viewed_at)}</TooltipContent>
              </Tooltip>
            }
          />
        ))}
        {isFetchingNextPage && <Skeleton className={"h-5 w-full"} />}
        <div ref={loaderAim}></div>
      </Stack>
    </Modal>
  );
}
